import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useQueryParams from 'hooks/shared/useQueryParams';
import { isValidActivationStepId } from 'components/pages/Banks/BankingSignUp/ActivationFlow/constants/activationStepIds';
import { useIsInActivationFlowExperiment } from './useIsInActivationFlowExperiment';

/**
 * Hook to handle activation flow resumption after external redirects (e.g., Swan)
 *
 * When external services (like Swan for card creation) redirect back to our app,
 * we need to resume the activation flow at the correct step.
 *
 * This hook:
 * 1. Checks if user is in A/B test group B (new sign up flow)
 * 2. Checks for `resumeActivation` query parameter on mount
 * 3. Validates the step ID against known step definitions
 * 4. If valid, triggers the provided callback
 * 5. Cleans up the query parameter from URL
 *
 * Usage:
 * ```tsx
 * useActivationFlowResumption((stepId) => {
 *   openActivationModal(stepId);
 * });
 * ```
 */
export function useActivationFlowResumption(
  onResume: (stepId: string) => void,
): void {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const hasProcessed = useRef(false);
  const isInActivationFlowExperiment = useIsInActivationFlowExperiment();

  useEffect(() => {
    // Only process once per mount
    if (hasProcessed.current) return;

    // Only process for users in group B (new activation flow)
    if (!isInActivationFlowExperiment) {
      // Still clean up the query parameter if present
      if (queryParams.resumeActivation) {
        cleanupUrlParam(navigate);
      }
      return;
    }

    const resumeStep = queryParams.resumeActivation;

    if (resumeStep && typeof resumeStep === 'string') {
      // Validate that the step ID exists in our step definitions
      if (!isValidActivationStepId(resumeStep)) {
        console.warn(
          `[Activation Flow] Invalid step ID in URL: "${resumeStep}". Ignoring resumption.`,
        );
        // Still clean up the invalid parameter from URL
        cleanupUrlParam(navigate);
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`[Activation Flow] Resuming at step: ${resumeStep}`);
      }

      // Mark as processed immediately to prevent double-trigger
      hasProcessed.current = true;

      // Trigger the resume callback
      onResume(resumeStep);

      // Clean up the query parameter from URL
      cleanupUrlParam(navigate);
    }
  }, [queryParams, navigate, onResume, isInActivationFlowExperiment]);
}

/**
 * Helper to clean up the resumeActivation query parameter from URL
 */
function cleanupUrlParam(navigate: ReturnType<typeof useNavigate>): void {
  // Use setTimeout to avoid triggering during render
  setTimeout(() => {
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete('resumeActivation');

    const newSearch = newParams.toString();
    const newUrl = newSearch
      ? `${window.location.pathname}?${newSearch}`
      : window.location.pathname;

    // Replace URL without triggering navigation
    navigate(newUrl, { replace: true });
  }, 0);
}

/**
 * Helper to create redirect URLs with activation flow resumption
 *
 * Usage:
 * ```tsx
 * createCard({
 *   successRedirectUri: createActivationResumptionURL('card_design'),
 *   errorRedirectUri: createActivationResumptionURL('card_design', { error: 'true' }),
 * })
 * ```
 */
export function createActivationResumptionURL(
  stepId: string,
  additionalParams?: Record<string, string>,
): string {
  const url = new URL(window.location.origin + window.location.pathname);

  // Add resumption parameter
  url.searchParams.set('resumeActivation', stepId);

  // Add any additional parameters
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.href;
}
